import React, { useState, useEffect, useCallback, useRef } from "react";
import { Trash2, X, CheckCircle, Loader2, Edit, ImageIcon } from "lucide-react";
import { DeleteConfirmModal } from "../ui/DeleteConfirmModal";

/**
 * A simplified card component for displaying and editing judge information
 * Designed to work with public S3 URLs (no signed URL complexity)
 */
export function JudgeCard({
  judge,
  isEditing,
  isUploading,
  uploadProgress,
  onEdit,
  onRemove,
  onSave,
  onPhotoChange,
  onCancel
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [localName, setLocalName] = useState(judge?.name || "");
  const [localTitle, setLocalTitle] = useState(judge?.title || "");
  const [localDefaultNote, setLocalDefaultNote] = useState(judge?.defaultNote || "");
  const judgeIdRef = useRef(judge?.id);

  // Reset local state when judge data changes or edit mode changes
  useEffect(() => {
    setLocalName(judge?.name || "");
    setLocalTitle(judge?.title || "");
    setLocalDefaultNote(judge?.defaultNote || "");
  }, [judge?.name, judge?.title, judge?.defaultNote, isEditing]);

  const handleSave = async () => {
    try {
      await onSave(judge.id, {
        name: localName,
        title: localTitle,
        defaultNote: localDefaultNote,
        photoUrl: judge.photoUrl || judge.photo || "", // Use existing photo URL
      });
      
      onEdit(null); // Exit edit mode after successful save
    } catch (error) {
      console.error("Error saving judge:", error);
    }
  };

  const handleCancel = () => {
    // Reset local state
    setLocalName(judge?.name || "");
    setLocalTitle(judge?.title || "");
    setLocalDefaultNote(judge?.defaultNote || "");
    
    // Use custom cancel handler if provided, otherwise default behavior
    if (onCancel) {
      onCancel(judge.id);
    } else {
      onEdit(null);
    }
  };

  const handleDelete = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    onRemove(judgeIdRef.current);
    setShowDeleteConfirm(false);
  }, [onRemove]);

  const handleFileSelect = useCallback(async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Basic validation
      if (file.size > 15 * 1024 * 1024) {
        alert('Image is too large. Please select an image under 15MB.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }

      // Call the photo change handler directly
      try {
        await onPhotoChange(judge.id, e);
      } catch (error) {
        console.error("Photo upload failed:", error);
        alert('Failed to upload photo. Please try again.');
      }
    }
  }, [judge.id, onPhotoChange]);

  // Get the photo URL - prefer photoUrl over photo for consistency
  const photoUrl = judge.photoUrl || judge.photo;

  return (
    <>
      <div className="card bg-gradient-to-b from-base-200 to-base-300 shadow-md hover:shadow-lg transition-all border border-base-300 w-full max-w-[250px]">
        <div className="card-body p-4">
          {/* Photo Container */}
          <div className="relative w-full flex justify-center mb-3">
            <div className="avatar">
              <div className="w-20 h-20 rounded-full border-2 border-primary bg-base-100 p-0.5">
                <div className="w-full h-full rounded-full overflow-hidden">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={judge.name || 'Judge'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.log('Image failed to load:', photoUrl);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  {/* Fallback placeholder */}
                  <div 
                    className="w-full h-full bg-base-300 flex items-center justify-center"
                    style={{ display: photoUrl ? 'none' : 'flex' }}
                  >
                    <ImageIcon className="w-6 h-6 opacity-50" />
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Progress Overlay */}
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[76px] h-[76px] rounded-full flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-base-100 opacity-70 rounded-full" />
                  <div className="z-10 flex flex-col items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin mb-1 text-primary" />
                    <span className="text-[10px] font-semibold">
                      {uploadProgress || 0}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Photo Upload Overlay - Only shown when editing and not uploading */}
            {isEditing && !isUploading && (
              <label className="absolute inset-0 flex items-center justify-center cursor-pointer group">
                <div className="w-[76px] h-[76px] rounded-full flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-base-100 opacity-0 hover:opacity-60 rounded-full transition-opacity" />
                  <div className="hidden group-hover:flex items-center justify-center z-10">
                    <div className="bg-primary text-primary-content px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap">
                      Upload Photo
                    </div>
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </label>
            )}
          </div>

          {/* Judge Information */}
          <div className="text-center space-y-1">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  className="input input-bordered input-sm w-full text-center"
                  value={localName}
                  onChange={(e) => setLocalName(e.target.value)}
                  placeholder="Judge Name"
                />
                <input
                  className="input input-bordered input-sm w-full text-center"
                  value={localTitle}
                  onChange={(e) => setLocalTitle(e.target.value)}
                  placeholder="Title/Specialty"
                />
                <textarea
                  className="textarea textarea-bordered textarea-sm w-full text-center resize-none"
                  value={localDefaultNote}
                  onChange={(e) => setLocalDefaultNote(e.target.value)}
                  placeholder="Default note for score cards..."
                  rows={2}
                />
              </div>
            ) : (
              <>
                <h3 className="font-medium text-base">
                  {judge.name || "Unnamed Judge"}
                </h3>
                <p className="text-base-content/70 text-xs">
                  {judge.title || "No Title"}
                </p>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="absolute top-2 right-2 flex gap-1">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="btn btn-ghost btn-xs btn-circle bg-base-100/80"
                  title="Cancel"
                >
                  <X className="w-3 h-3" />
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-ghost btn-xs btn-circle text-success bg-base-100/80"
                  title="Save changes"
                  disabled={!localName.trim()}
                >
                  <CheckCircle className="w-3 h-3" />
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-ghost btn-xs btn-circle text-error bg-base-100/80"
                  title="Delete judge"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            ) : (
              <button
                onClick={() => onEdit(judge.id)}
                className="btn btn-ghost btn-xs btn-circle bg-base-100/80"
                title="Edit judge"
              >
                <Edit className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        itemName={judge.name}
        itemType="judge"
      />
    </>
  );
} 